import React, { useEffect, useState } from "react";
import NetworkGraph from "./NetworkGraph";
import { useSelector } from "react-redux";

const MultiClusterNetworkGraph = ({
  metadata,
  single,
  networkLinkLength,
  style,
  id,
  chartGlobalDataLabel,
  chartGradientColor,
  chartMarkers,
  widget_index,
  section_index,
  PdfDownloadStatus,
  template_id,
}) => {
  const authParams = useSelector((state) => state?.Authentication?.authParams);

  const [networkClusterConfig, setNetworkClusterConfig] = useState(null);
  const [dataProcessing, setDataProcessing] = useState(true);
  // If magnitude value in positive then it will calculate lighter color
  // else calculate darker color
  const calculateNewShadeOfColor = (hexColor, magnitude) => {
    hexColor = hexColor?.replace(`#`, ``);
    if (hexColor?.length < 6) {
      for (let i = hexColor?.length; i < 6; i++) {
        hexColor += "0";
      }
    }
    if (hexColor?.length === 6) {
      const decimalColor = parseInt(hexColor, 16);
      let r = (decimalColor >> 16) + magnitude;
      r > 255 && (r = 255);
      r < 0 && (r = 0);
      let g = (decimalColor & 0x0000ff) + magnitude;
      g > 255 && (g = 255);
      g < 0 && (g = 0);
      let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
      b > 255 && (b = 255);
      b < 0 && (b = 0);
      return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
    } else {
      return "#" + hexColor;
    }
  };
  const newColorShadeInHex6 = (hexColor, magnitude) => {
    let newColor = calculateNewShadeOfColor(hexColor, magnitude);
    newColor = newColor?.replace(`#`, ``);
    if (newColor?.length < 6) {
      for (let i = newColor?.length; i < 6; i++) {
        newColor += "0";
      }
    }
    return "#" + newColor;
  };
  const globalChartColor = [
    "#6060FF",
    "#9D48FF",
    "#FF00FF",
    "#DFAAAA",
    "#29CFCF",
    "#FF7D6C",
    "#FF7D6C",
    "#CD97FF",
    "#FFD58B",
    "#75FF75",
    "#FFC0CB",
    "#C66060",
    "#A5A5DC",
    "#00A669",
    "#00A669",
    "#9898FF",
    "#EEEE52",
    "#FF83F7",
    "#3FE7AC",
    "#B600AA",
    "#39A7E4",
    "#6600DA",
    "#FFB940",
    "#FF4040",
    "#24E224",
    "#FFDB4D",
    "#CE9ECE",
    "#1C9F00",
    "#FFC1B9",
    "#FF9C73",
    "#5656B2",
    "#FF6E31",
    "#9FE9E9",
  ];

  const getSingleNetworkClusterData = (
    clusters,
    numOfNetwork,
    cluster_index,
    graphID,
    offset_index
  ) => {
    let singleCluster = {
      id: "",
      data: [],
    };
    if (
      clusters?.data &&
      Array.isArray(clusters?.data) &&
      clusters?.data?.length > 0
    ) {
      singleCluster.id =
        (graphID ? graphID : "network_graph") + "$N$" + cluster_index;
      singleCluster.name =
        (graphID ? graphID : "network_graph") + "$N$" + cluster_index;

      const colorArray = globalChartColor;
      let j = offset_index ? offset_index : 0;
      let noOfIterationInColors = 0;
      let lengthOfColors = colorArray?.length;
      singleCluster.nodes = Object.keys(clusters?.nodes).map(function (
        id,
        index
      ) {
        let dataColor;
        let node = clusters?.nodes?.[id];
        if (
          node?.color !== undefined &&
          node?.color !== "#000000" &&
          node?.color !== null
        ) {
          dataColor = node?.color;
        } else if (
          node?.link_color !== undefined &&
          node?.link_color !== "#000000" &&
          node?.link_color !== null &&
          node?.link_color
        ) {
          dataColor = node?.link_color;
        } else {
          if (j >= lengthOfColors) {
            noOfIterationInColors = parseInt(j / lengthOfColors);
            let rem = parseInt(j % lengthOfColors);
            dataColor =
              noOfIterationInColors > 0
                ? newColorShadeInHex6(
                    globalChartColor[rem],
                    parseInt(noOfIterationInColors * 5)
                  )
                : globalChartColor[j];
          } else {
            dataColor = globalChartColor[j];
          }
          j++;
        }
        let lighterColor = newColorShadeInHex6(dataColor, 60);
        let darkerColor = dataColor;
        // here calculate darker text color for showing in tooltip
        let darkerTextColor = newColorShadeInHex6(dataColor, -80);
        if (
          node?.link_color !== undefined &&
          node?.link_color !== "#000000" &&
          node?.link_color !== null &&
          !singleCluster?.link
        ) {
          singleCluster.link = {
            color: node?.link_color,
          };
        } else if (!singleCluster?.link) {
          singleCluster.link = {
            color: dataColor,
          };
        }
        // for tooltip on hover

        let clr = chartGradientColor
          ? {
              radialGradient: {
                cx: 0.5,
                cy: 0.3,
                r: 0.7,
              },
              stops: [
                [0, lighterColor],
                [1, darkerColor], // darken
              ],
            }
          : lighterColor;
        let yTooltipValue = node?.node_value >= 0 ? node?.node_value : null;
        let yPercentValue = node?.percent >= 0 ? node?.percent : 0;
        let xTooltipValue = node?.node_name
          ? node?.node_name
          : node?.AuthorScreenName;
        let tooltipContent =
          "<div class='tooltip_chart--dark d-flex align-items-center fontSize-14 font-weight600'><span class='text-Darkgray  d-block mr-1'>" +
          xTooltipValue +
          " </span><span class='stackbar__tooltip' style='background-color:" +
          clr +
          ";color:" +
          darkerTextColor +
          ";'>" +
          yTooltipValue?.toLocaleString() +
          (yPercentValue
            ? " | " + yPercentValue?.toFixed(1)?.replace(/\.0$/, "") + "%"
            : "") +
          "</span></div>" +
          (node?.AuthorBio
            ? `<div className='text-Darkgray font-weight600 w-100 mt-1'>Bio</div><div className='text_regular break-word author_bio' style='max-width:${"30rem"};word-wrap: break-word;white-space:break-spaces;'>${
                node?.AuthorBio
              }</div>`
            : "<div className='text-grayLight fontSize-14 d-flex justify-content-center align-items-center pt-15 pb-15 w-100'>Bio not found</div>");

        let element = document.createElement("div");
        element?.classList?.add("custom_background");
        element.innerHTML = tooltipContent;
        return {
          id: singleCluster?.id + "$N$" + id,
          name: node?.node_name ? node?.node_name : node?.AuthorScreenName,
          label: chartGlobalDataLabel
            ? node?.node_name
              ? node?.node_name
              : node?.AuthorScreenName
            : "",
          shape: node?.node_image ? "circularImage" : "circle",
          image: node?.node_image
            ? node?.node_image
            : node?.u_picurl
            ? node?.u_picurl
            : null,
          // brokenImage: node?.node_image ? node?.node_image : node?.u_picurl,
          title: element,
          // marker: {
          //   enabled: true,
          //   radius:
          //     el?.radius > 100
          //       ? 100
          //       : el?.radius < 20
          //       ? 20
          //       : parseInt(el?.radius),
          //   // symbol: "circle",
          //   // symbol: base64ImageFromUrl ? `url(${base64ImageFromUrl})` : "circle",
          //   symbol: "url(" + el?.u_picurl + ")",
          //   width:
          //     el?.radius > 100
          //       ? 100
          //       : el?.radius < 20
          //       ? 20
          //       : parseInt(el?.radius),
          //   height:
          //     el?.radius > 100
          //       ? 100
          //       : el?.radius < 20
          //       ? 20
          //       : parseInt(el?.radius),
          //   lineColor: el?.link_color,
          // },
          color: {
            border: darkerColor,
            background: chartGradientColor
              ? {
                  radialGradient: {
                    cx: 0.5,
                    cy: 0.3,
                    r: 0.7,
                  },
                  stops: [
                    [0, lighterColor],
                    [1, darkerColor], // darken
                  ],
                }
              : lighterColor,
          },
          font: { color: darkerColor },
          size:
            node?.radius > 100
              ? 100
              : node?.radius < 20
              ? 20
              : parseInt(node?.radius),
        };
      });
      singleCluster.data =
        clusters?.data &&
        Array.isArray(clusters?.data) &&
        clusters?.data?.map((d) => {
          return {
            from: singleCluster?.id + "$N$" + d?.from,
            to: singleCluster?.id + "$N$" + d?.to,
            color: singleCluster?.link?.color
              ? singleCluster?.link?.color
              : d?.link_color
              ? d?.link_color
              : null,
            length: d?.link_length ? d?.link_length : null,
          };
        });
    }
    return singleCluster;
  };

  // This is used for creating whole network data of Network Graph Charts
  useEffect(() => {
    let updatedNodes = [];
    let updatedEdges = [];
    if (single && Array.isArray(single)) {
      let offset_starting_index = 0;
      single?.forEach((cluster, cluster_index) => {
        offset_starting_index =
          cluster_index > 0
            ? offset_starting_index +
              (cluster?.nodes && Object.keys(cluster?.nodes)?.length)
            : 0;
        let singleClusterData = getSingleNetworkClusterData(
          cluster,
          single?.length,
          cluster_index,
          id,
          offset_starting_index
        );
        if (
          singleClusterData?.data &&
          Array.isArray(singleClusterData?.data) &&
          singleClusterData?.data?.length > 0
        ) {
          updatedEdges = [...updatedEdges, ...singleClusterData?.data];
        }
        if (
          singleClusterData?.nodes &&
          Array.isArray(singleClusterData?.nodes) &&
          singleClusterData?.nodes?.length > 0
        ) {
          updatedNodes = [...updatedNodes, ...singleClusterData?.nodes];
        }
      });
    } else if (single && single?.data) {
      let singleClusterData = getSingleNetworkClusterData(single, 1, 0, id, 0);
      if (
        singleClusterData?.data &&
        Array.isArray(singleClusterData?.data) &&
        singleClusterData?.data?.length > 0
      ) {
        updatedEdges = [...updatedEdges, ...singleClusterData?.data];
      }
      if (
        singleClusterData?.nodes &&
        Array.isArray(singleClusterData?.nodes) &&
        singleClusterData?.nodes?.length > 0
      ) {
        updatedNodes = [...updatedNodes, ...singleClusterData?.nodes];
      }
    }
    if (updatedNodes?.length > 0 && updatedEdges?.length > 0) {
      setNetworkClusterConfig({
        nodes: updatedNodes,
        edges: updatedEdges,
      });
    }
  }, [single, chartGlobalDataLabel]);

  // This is used, whenever we update networkClusterConfig need to console
  // useEffect(() => {
  //   // console.log(
  //   //   "useEffect got called : networkClusterConfig : ",
  //   //   networkClusterConfig
  //   // );
  // }, [networkClusterConfig]);

  const options = {
    autoResize: true,
    layout: {
      improvedLayout: false, // Enable improved layout algorithm
    },

    // animation: true, // default duration is 1000ms and default easingFunction is easeInOutQuad.
    // animation: {
    //   duration: 5000,
    //   easingFunction: "linear",
    //   scale: 2.0,
    // },

    nodes: {
      borderWidth: 4,
      size: 15,
      color: {
        border: "#222222",
        background: "#666666",
      },
      font: { color: "black" },
    },
    edges: {
      smooth: {
        forceDirection: "none",
        roundness: 0,
      },
    },
    // physics: {
    //   minVelocity: 0.75,
    //   solver: "repulsion",
    //   timestep: 0.51,
    //   // barnesHut: {
    //   //   theta: 0.55,
    //   //   gravitationalConstant: -1250,
    //   //   centralGravity: 0.35,
    //   //   springLength: 185,
    //   //   springConstant: 0.27,
    //   //   damping: 0.18,
    //   //   avoidOverlap: 1,
    //   // },
    // },

    interaction: {
      hover: true,
      navigationButtons: true,
      keyboard: false,
    },
  };

  return (
    <>
      {networkClusterConfig && networkClusterConfig?.nodes?.length > 0 ? (
        <NetworkGraph
          nodes={networkClusterConfig?.nodes}
          edges={networkClusterConfig?.edges}
          options={options}
          id={id}
          style={style}
          single={single}
        />
      ) : (
        "No Data"
      )}
    </>
  );
};

export default React.memo(MultiClusterNetworkGraph);
